"use client";

import React, { useCallback, useEffect, useMemo, useState, FC } from "react";

import Box from "@mui/material/Box";
import clsx from "clsx";

import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useWindowScroll } from "~/hooks/use-window-scroll/index";
import { ScrollDirection } from "~/hooks/use-window-scroll/types";
import { Up16 } from "~/theme/icons/components/up";

import { BACK_TO_TOP_BUTTON_CLASSES } from "./constants";
import { BackToTopButtonContainer } from "./styled";

const SCROLL_OFFSET_THRESHOLD = 1024;

export interface BackToTopButtonProps {
  name?: string;
}

export const BackToTopButton: FC<BackToTopButtonProps> = ({ name = "to top" }) => {
  const [isInitiated, setIsInitiated] = useState(false);

  const { scrollTop, scrollDirection } = useWindowScroll();

  const isScrollingUp = useMemo(() => {
    return scrollDirection === ScrollDirection.UP;
  }, [scrollDirection]);

  const scrollToTop = useCallback(() => {
    let isScrolling = true;

    const scrollStep = () => {
      if (!isScrolling) {
        return;
      }

      const currentScroll =
        document.documentElement.scrollTop || document.body.scrollTop;
      if (currentScroll > 0) {
        const step = Math.max(50, currentScroll / 5); // Increase step size for faster scrolling
        scrollTo(0, currentScroll - step);
        requestAnimationFrame(scrollStep);
      }
    };

    const stopScrolling = () => (isScrolling = false);

    addEventListener("wheel", stopScrolling, { once: true });
    addEventListener("touchmove", stopScrolling, { once: true });

    requestAnimationFrame(scrollStep);

    return () => {
      removeEventListener("wheel", stopScrolling);
      removeEventListener("touchmove", stopScrolling);
    };
  }, []);

  useEffect(() => {
    if (scrollTop > SCROLL_OFFSET_THRESHOLD && isScrollingUp) {
      setIsInitiated(true);
    } else {
      setIsInitiated(false);
    }
  }, [scrollTop, isScrollingUp]);

  return (
    <BackToTopButtonContainer
      aria-label={name}
      className={clsx(
        { [BACK_TO_TOP_BUTTON_CLASSES.isVisible]: isInitiated },
        { [BACK_TO_TOP_BUTTON_CLASSES.isHidden]: !isInitiated },
      )}
      onClick={scrollToTop}
      {...getTestAutomationProps("back-to-top-button")}
    >
      <Box className={BACK_TO_TOP_BUTTON_CLASSES.up}>
        <Up16 />
      </Box>
    </BackToTopButtonContainer>
  );
};
